// theme/assets/frame-picker.js
//
// Handlar interaktionen i snippets/frame-picker.liquid:
//   1. När kundens canvas-storlek ändras → uppdatera compat-state
//      (cirklar struck om size inte i FRAME_COMPATIBLE_SIZES).
//   2. När en ramfärg väljs → hoppa i galleriet till tavlan i den ramen om
//      produkten har ramade bilder (snippets/artiley-ramade-bilder.liquid),
//      annars swappa hero-bilden till ramproduktens featured_image
//      (window.__frameImages[color]).
//   3. När kunden klickar "Lägg i kundvagn" och en ramfärg är vald →
//      hooka submit, lägg canvas + ram via /cart/add.js (bulk add).
//   4. Priskonvergens: MutationObserver på pris-blocket re-applicerar
//      totalpriset (canvas + ram) när Habitats section-render skriver
//      över det med canvas-only-pris (race:en utredd 2026-08-13, se
//      docs/frame-pipeline/investigations/2026-08-13-frame-picker-pris-race.md).
//
// Bilder läses från window.__frameImages (genererat av Liquid från
// ramprodukternas featured_image), INTE från AI-genererade
// frame-edge-mockups som tidigare. Strategin ändrades 2026-05-04.

(function () {
  'use strict';

  // Storleksguide-popup lämnas på sin default-position (egen rad efter
  // variant-knapparna). Försök att hoista inline med "Storlek:"-rubriken
  // visade sig fragilt cross-browser pga <legend>-elementets special-
  // styling (display:table från app.css + flex/float-quirks).

  const root = document.querySelector('[data-frame-picker]');
  if (!root) return;

  const FRAME_COMPATIBLE_SIZES = new Set([
    '50x50','50x70','70x50','70x100','100x70','80x80','90x130','130x90','100x100'
  ]);

  // Hero-bilden: leta efter den aktiva sliden i Habitat-temats produktslider.
  // Habitat strukturerar product gallery som .product-images__slide(.is-selected).
  const heroSlide = document.querySelector('.product-images__slide.is-selected, .product-images__slide.is-initial-selected');
  const heroImg = heroSlide ? heroSlide.querySelector('img') : null;
  const originalHeroSrc = heroImg ? heroImg.getAttribute('src') : null;
  const originalHeroSrcset = heroImg ? heroImg.getAttribute('srcset') : null;

  // Ramade bilder i galleriet (snippets/artiley-ramade-bilder.liquid, data-ramad-bild="<färg>"):
  // då hoppar bildspelet till tavlan i vald ram i stället för att byta första bilden.
  const gallery = document.querySelector('product-slider');
  const hasFramedSlides = !!(gallery && gallery.querySelector('[data-ramad-bild]'));

  let currentFrame = 'none';

  function normalizeSize(raw) {
    if (!raw) return '';
    return String(raw).toLowerCase().replace(/\s|cm/g, '').replace('×', 'x');
  }

  // Ramen är fysiskt samma list oavsett orientering — en 70×100-ram är
  // identisk med en 100×70-ram, bara monterad åt andra hållet. Shopify
  // har portrait/kvadrat-varianter (50x50, 50x70, 70x100, 80x80, 90x130,
  // 100x100), så för canvas i landskap-storlek (HxB-notation: 70x50, 100x70
  // och 130x90 = porträtt-canvas) mappar vi till motsvarande ramnyckel.
  // Annars no-op.
  const FRAME_KEY_ALIASES = { '100x70': '70x100', '130x90': '90x130', '70x50': '50x70' };
  function getFrameKey(canvasSize) {
    return FRAME_KEY_ALIASES[canvasSize] || canvasSize;
  }

  function formatPrice(cents) {
    // Prisbelopp i pickern — formateras med marknadens money-format
    // (window.__frameMoneyFormat renderas per market av Liquid), så
    // EUR-marknader ser "€92,95" och .se ser "929 kr". Översatt prefix
    // ("+ {{ price }}" / "från {{ price }}") läses från data-attributes.
    return formatShopifyMoney(cents, window.__frameMoneyFormat);
  }

  function applyPriceTemplate(template, formattedPrice) {
    if (!template) return formattedPrice;
    return template.replace(/\{\{\s*price\s*\}\}/g, formattedPrice);
  }

  function withThousandsSep(numStr, sep) {
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return parts.join('.');
  }

  // Implementerar Shopify-likt money_format-format för totalprisuppdateringen
  // i .price-blocket (måste matcha Habitats befintliga formattering så det inte
  // ser ut som ett oväntat formatbyte när användaren klickar).
  //
  // Placeholders UTAN explicit separator ("amount", "amount_no_decimals")
  // lokaliserar servern efter buyer-locale (sv → "1 323", de → "1.323") —
  // formatsträngen "{{amount_no_decimals}} SEK" renderas alltså som
  // "1 323 SEK" av Liquid. Matcha via Intl + dokumentets lang. Placeholders
  // MED explicit separator (with_comma/with_space) förblir bokstavliga.
  function localizedNumber(major, decimals) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(major);
    } catch (err) {
      return major.toFixed(decimals);
    }
  }

  function formatShopifyMoney(cents, formatString) {
    const fmt = formatString || '{{ amount }} kr';
    const major = cents / 100;
    if (fmt.indexOf('amount_no_decimals_with_comma_separator') !== -1) {
      return fmt.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        withThousandsSep(major.toFixed(0), '.'));
    }
    if (fmt.indexOf('amount_no_decimals_with_space_separator') !== -1) {
      return fmt.replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g,
        withThousandsSep(major.toFixed(0), ' '));
    }
    if (fmt.indexOf('amount_with_comma_separator') !== -1) {
      const withDot = withThousandsSep(major.toFixed(2), '.');
      // Komma som decimal: "1.234.56" → "1.234,56" — byt sista punkt till komma.
      const swapped = withDot.replace(/\.(\d{2})$/, ',$1');
      return fmt.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, swapped);
    }
    if (fmt.indexOf('amount_with_space_separator') !== -1) {
      return fmt.replace(/\{\{\s*amount_with_space_separator\s*\}\}/g,
        withThousandsSep(major.toFixed(2), ' '));
    }
    if (fmt.indexOf('amount_no_decimals') !== -1) {
      return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, localizedNumber(major, 0));
    }
    return fmt.replace(/\{\{\s*amount\s*\}\}/g, localizedNumber(major, 2));
  }

  function getSelectedCanvasVariant() {
    const variantsScript = document.querySelector('variant-selects script[type="application/json"]');
    const variantInput = document.querySelector('[name="id"]');
    if (!variantsScript || !variantInput) return null;
    try {
      const variants = JSON.parse(variantsScript.textContent);
      return variants.find((v) => String(v.id) === String(variantInput.value)) || null;
    } catch (err) {
      return null;
    }
  }

  function applyTotalPrice() {
    const canvas = getSelectedCanvasVariant();
    if (!canvas || typeof canvas.price !== 'number') return;
    const canvasSize = root.getAttribute('data-canvas-size');
    const frameKey = getFrameKey(canvasSize);
    const variants = window.__frameVariants || {};
    const frame = currentFrame !== 'none'
      ? (variants[currentFrame] && variants[currentFrame][frameKey])
      : null;
    const framePrice = frame && typeof frame.price === 'number' ? frame.price : 0;

    // Aktuellt pris (<ins>): canvas.price + ev. ramens pris.
    // write-if-different: prisobservern (nedan) kör applyTotalPrice vid
    // varje främmande DOM-skrivning — skriv bara vid avvikelse så vår
    // egen skrivning inte genererar nya mutationer (konvergens, ej loop).
    const totalCents = canvas.price + framePrice;
    const totalFormatted = formatShopifyMoney(totalCents, window.__frameMoneyFormat);
    document.querySelectorAll('[id^="price-"] ins .amount').forEach((node) => {
      if (node.textContent !== totalFormatted) node.textContent = totalFormatted;
    });

    // Compare-at (<del>, överstruket originalpris): bara uppdatera om
    // canvas-varianten har compare_at_price (= på rea). Ramen är aldrig
    // på rea, så ramens pris adderas oavsett.
    // Utan denna fix visade compare-at bara canvas-original — det såg
    // ut som om ramen var "gratis" jämfört med originalet.
    if (typeof canvas.compare_at_price === 'number' && canvas.compare_at_price > 0) {
      const compareCents = canvas.compare_at_price + framePrice;
      const compareFormatted = formatShopifyMoney(compareCents, window.__frameMoneyFormat);
      document.querySelectorAll('[id^="price-"] del .amount').forEach((node) => {
        if (node.textContent !== compareFormatted) node.textContent = compareFormatted;
      });
    }
  }

  function getMinPrice(color) {
    const variants = (window.__frameVariants || {})[color] || {};
    const prices = Object.values(variants)
      .map((v) => (v && typeof v.price === 'number' ? v.price : null))
      .filter((p) => p !== null);
    return prices.length ? Math.min.apply(null, prices) : null;
  }

  // ─── LAGERNOTIS ("åter i lager") ────────────────────────────────────
  //
  // Beräknat återkomstdatum per ramfärg kommer från window.__frameRestock
  // (Liquid → produktmetafältet custom.restock_date på ramprodukten).
  // Returnerar 'YYYY-MM-DD' bara om datumet finns OCH ligger i framtiden;
  // ett passerat datum ska inte stå kvar som ett löfte på sidan.
  function getRestockDate(color) {
    const raw = (window.__frameRestock || {})[color];
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const today = new Date();
    const todayIso = today.getFullYear() + '-'
      + String(today.getMonth() + 1).padStart(2, '0') + '-'
      + String(today.getDate()).padStart(2, '0');
    return raw >= todayIso ? raw : null;
  }

  // "2026-09-25" → "25 september" (sv) / "September 25" (en) via Intl i
  // butikens aktiva locale (data-locale = request.locale.iso_code).
  function formatRestockDate(iso) {
    try {
      const d = new Date(iso + 'T12:00:00');
      return new Intl.DateTimeFormat(root.getAttribute('data-locale') || document.documentElement.lang || undefined, {
        day: 'numeric', month: 'long'
      }).format(d);
    } catch (err) {
      return iso;
    }
  }

  // Storleksnyckel "70x100" → "70×100 cm" för löptext.
  function formatSizeLabel(sizeKey) {
    return String(sizeKey || '').replace('x', '×') + ' cm';
  }

  // Bygger notistext ur en locale-mall med {{ frame }}, {{ size }} och
  // {{ date }}. Ramnamnet fetstilas; allt sätts som text (aldrig innerHTML).
  function renderHintTemplate(template, values) {
    const frag = document.createDocumentFragment();
    const re = /\{\{\s*(frame|size|date)\s*\}\}/g;
    let last = 0;
    let m;
    while ((m = re.exec(template)) !== null) {
      if (m.index > last) frag.appendChild(document.createTextNode(template.slice(last, m.index)));
      const val = values[m[1]] || '';
      if (m[1] === 'frame') {
        const strong = document.createElement('strong');
        strong.textContent = val;
        frag.appendChild(strong);
      } else {
        frag.appendChild(document.createTextNode(val));
      }
      last = m.index + m[0].length;
    }
    if (last < template.length) frag.appendChild(document.createTextNode(template.slice(last)));
    return frag;
  }

  // Text för en slut-i-lager-ram (används både i notisen och som tooltip).
  function buildSoldoutHint(color, frameKey, asText) {
    const btn = root.querySelector('.frame-picker__option[data-frame-color="' + color + '"]');
    const frameName = btn ? (btn.getAttribute('data-frame-display-name') || color) : color;
    const restock = getRestockDate(color);
    const template = restock
      ? (root.getAttribute('data-restock-hint') || '')
      : (root.getAttribute('data-soldout-hint') || '');
    if (!template) return asText ? (root.getAttribute('data-out-of-stock') || '') : null;
    const values = {
      frame: frameName,
      size: formatSizeLabel(frameKey),
      date: restock ? formatRestockDate(restock) : ''
    };
    if (asText) {
      return template.replace(/\{\{\s*(frame|size|date)\s*\}\}/g, (_, k) => values[k] || '');
    }
    return renderHintTemplate(template, values);
  }

  // Fyller/döljer <p data-frame-notice> utifrån aktuell storlek: en rad per
  // ramfärg som är slut i lager för den storleken. Körs vid varje
  // storleksbyte och när live-tillgänglighet hämtats (refreshMarketPrices).
  function updateStockNotice() {
    const node = root.querySelector('[data-frame-notice]');
    if (!node) return;
    const compatible = root.getAttribute('data-compatible') === 'true';
    const frameKey = getFrameKey(root.getAttribute('data-canvas-size'));
    const variants = window.__frameVariants || {};

    while (node.firstChild) node.removeChild(node.firstChild);
    if (!compatible) { node.hidden = true; return; }

    let lines = 0;
    root.querySelectorAll('.frame-picker__option:not(.frame-picker__option--none)').forEach((btn) => {
      const color = btn.dataset.frameColor;
      const v = variants[color] && variants[color][frameKey];
      // Saknad variant = inte i sortimentet för storleken, ingen notis.
      if (!v || v.available !== false) return;
      const frag = buildSoldoutHint(color, frameKey, false);
      if (!frag) return;
      const line = document.createElement('span');
      line.className = 'frame-picker__notice__line';
      line.appendChild(frag);
      node.appendChild(line);
      lines += 1;
    });
    node.hidden = lines === 0;
  }

  // v2: pris/hint visas på en ENDA plats (data-frame-price-display)
  // till höger om sista cirkeln, inte på varje pill. Logik:
  //   inkompatibel storlek  → hint-text ("Tillgänglig från 70×100 cm")
  //   kompatibel + Ingen    → tomt
  //   kompatibel + ramfärg  → "+ X kr"
  function updatePriceDisplay() {
    const node = root.querySelector('[data-frame-price-display]');
    if (!node) return;
    const compatible = root.getAttribute('data-compatible') === 'true';
    const tplAddon = root.getAttribute('data-price-addon') || '+ {{ price }}';
    const hintUnavailable = root.getAttribute('data-hint-unavailable') || '';

    if (!compatible) {
      node.textContent = hintUnavailable;
      node.classList.add('is-hint');
      return;
    }
    node.classList.remove('is-hint');

    if (currentFrame === 'none') {
      node.textContent = '';
      return;
    }

    const variants = window.__frameVariants || {};
    const canvasSize = root.getAttribute('data-canvas-size');
    const frameKey = getFrameKey(canvasSize);
    const v = variants[currentFrame] && variants[currentFrame][frameKey];

    // Slut i lager för vald ram + storlek — visa "Tillfälligt slut" istället
    // för pris. Använder is-hint-stilen så texten matchar inkompatibel-storleks-
    // hinten visuellt (grå, samma typografi).
    if (v && v.available === false) {
      node.textContent = root.getAttribute('data-out-of-stock') || 'Tillfälligt slut';
      node.classList.add('is-hint');
      return;
    }

    if (v && typeof v.price === 'number') {
      node.textContent = applyPriceTemplate(tplAddon, formatPrice(v.price));
    } else {
      node.textContent = '';
    }
  }

  function syncCanvasSize() {
    const variantInput = document.querySelector('[name="id"]');
    let size = '';

    if (variantInput) {
      const variantId = variantInput.value;
      // Habitat exposerar product.variants som JSON i variant-selects > script
      const variantsScript = document.querySelector('variant-selects script[type="application/json"]');
      if (variantsScript) {
        try {
          const variants = JSON.parse(variantsScript.textContent);
          const match = variants.find((v) => String(v.id) === String(variantId));
          if (match) size = normalizeSize(match.option1);
        } catch (err) {
          // ignorera parse-fel — fallback nedan
        }
      }
    }

    // Fallback: läs från första aktiva variant_radio/select.
    if (!size) {
      const activeOption = document.querySelector('input[name^="options"]:checked, select[name^="options"]');
      if (activeOption) size = normalizeSize(activeOption.value);
    }

    root.setAttribute('data-canvas-size', size);
    const compatible = FRAME_COMPATIBLE_SIZES.has(size);
    root.setAttribute('data-compatible', String(compatible));

    // Per-färg state: knappen blir is-disabled antingen pga inkompatibel
    // storlek (alla färger samtidigt) ELLER för att den specifika
    // (färg, storlek)-kombinationen är slut i lager. Hover-tooltipen
    // skiljer sig: "Tillgänglig från X cm" vs "Tillfälligt slut".
    const variants = window.__frameVariants || {};
    const frameKey = getFrameKey(size);
    const hintUnavailable = root.getAttribute('data-hint-unavailable') || '';

    root.querySelectorAll('.frame-picker__option:not(.frame-picker__option--none)').forEach((btn) => {
      const color = btn.dataset.frameColor;
      const v = variants[color] && variants[color][frameKey];
      // Saknad variant (raderad/omdöpt i Shopify) behandlas som slut i lager —
      // en valbar swatch utan variant leder annars till canvas-only-add i checkout.
      const outOfStock = !v || v.available === false;
      const disabled = !compatible || outOfStock;

      btn.classList.toggle('is-disabled', disabled);
      btn.classList.toggle('is-soldout', compatible && outOfStock);
      btn.setAttribute('aria-disabled', String(disabled));
      if (disabled) {
        btn.setAttribute('tabindex', '-1');
        if (outOfStock && compatible) {
          btn.setAttribute('title', (v && buildSoldoutHint(color, frameKey, true)) || root.getAttribute('data-out-of-stock') || 'Tillfälligt slut');
        } else {
          btn.setAttribute('title', hintUnavailable);
        }
      } else {
        btn.removeAttribute('tabindex');
        const displayName = btn.getAttribute('data-frame-display-name') || '';
        btn.setAttribute('title', displayName);
      }
    });

    updateStockNotice();

    // v2: hint-text visas i price-display-noden (inte ett separat <p>).
    // Eventuell residual <p class="frame-picker__hint"> från äldre version
    // tas bort defensivt.
    const oldHint = root.querySelector('.frame-picker__hint');
    if (oldHint) oldHint.remove();

    // Återställ till "Ingen" om vald ram blir otillgänglig (inkompatibel
    // storlek ELLER slut i lager för nya storleken).
    const currentVariant = currentFrame !== 'none'
      ? (variants[currentFrame] && variants[currentFrame][frameKey])
      : null;
    const currentOutOfStock = currentFrame !== 'none'
      && (!currentVariant || currentVariant.available === false);
    if (currentFrame !== 'none' && (!compatible || currentOutOfStock)) {
      selectFrame('none');
    } else {
      applyTotalPrice();
      syncBuyNowVisibility();
      updatePriceDisplay();
    }
  }

  // Hoppa i bildspelet (Flickity i Habitats <product-slider>) till tavlan i vald ram. Vid "Ingen"
  // tillbaka till första bilden, men bara om en ramad bild visas just nu.
  function showFramedSlide(color, fromClick) {
    const flkty = gallery && gallery.flkty;
    if (!flkty) return;
    const cells = flkty.cells.map((c) => c.element);
    // Utan animation: annars glider bildspelet förbi närbilderna innan det stannar.
    if (color === 'none') {
      const shown = cells[flkty.selectedIndex];
      if (shown && shown.hasAttribute('data-ramad-bild')) flkty.select(0, false, true);
      return;
    }
    const index = cells.findIndex((el) => el.getAttribute('data-ramad-bild') === color);
    if (index < 0) return;
    flkty.select(index, false, true);
    if (fromClick) scrollGalleryIntoView();
  }

  // De ramade bilderna laddas lat (lazysizes) och visar annars en suddig 20 px-förhandsbild i upp till
  // en sekund efter ramvalet. Ladda dem när ramväljaren närmar sig skärmen (efter sidans load, så att
  // första bilden inte får konkurrens), eller senast när kunden rör vid väljaren.
  if (hasFramedSlides) {
    let preloaded = false;
    const preloadFramed = () => {
      if (preloaded) return;
      preloaded = true;
      gallery.querySelectorAll('[data-ramad-bild] img.lazyload').forEach((img) => {
        if (window.lazySizes && window.lazySizes.loader) window.lazySizes.loader.unveil(img);
      });
    };
    root.addEventListener('pointerdown', preloadFramed, { once: true });
    root.addEventListener('focusin', preloadFramed, { once: true });
    const watchPicker = () => {
      if (!('IntersectionObserver' in window)) return preloadFramed();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); preloadFramed(); }
      }, { rootMargin: '400px 0px' });
      io.observe(root);
    };
    if (document.readyState === 'complete') watchPicker();
    else window.addEventListener('load', watchPicker, { once: true });
  }

  // Miniatyrraden på datorn rymmer åtta bilder och de ramade gör dem fler. Temat rullar raden
  // tillbaka till vänsterkanten vid varje bildbyte, så rulla vald miniatyr till mitten i sidled.
  const thumbRow = hasFramedSlides ? document.querySelector('#Product-Thumbnails') : null;
  if (thumbRow) {
    new MutationObserver((mutations) => {
      if (!mutations.some((m) => m.target.classList && m.target.classList.contains('product-thumbnail'))) return;
      const thumb = thumbRow.querySelector('.product-thumbnail.is-initial-selected');
      if (!thumb || thumbRow.scrollWidth <= thumbRow.clientWidth) return;
      setTimeout(() => {
        const offset = thumb.getBoundingClientRect().left - thumbRow.getBoundingClientRect().left;
        const left = thumbRow.scrollLeft + offset - (thumbRow.clientWidth - thumb.offsetWidth) / 2;
        thumbRow.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }, 60);
    }).observe(thumbRow, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  // I mobilen ligger ramväljaren under bilden. Syns inte större delen av bilden rullar sidan
  // upp till den, under det fasta sidhuvudet. På datorn står bilden still bredvid (sticky).
  function scrollGalleryIntoView() {
    const header = document.querySelector('.header-section');
    const headerPos = header ? getComputedStyle(header).position : '';
    const offset = header && (headerPos === 'sticky' || headerPos === 'fixed') ? header.offsetHeight : 0;
    const r = gallery.getBoundingClientRect();
    const visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, offset);
    if (visible >= r.height * 0.6) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: window.scrollY + r.top - offset - 8, behavior: reduce ? 'auto' : 'smooth' });
  }

  function selectFrame(color, fromClick) {
    currentFrame = color;

    root.querySelectorAll('.frame-picker__option').forEach((btn) => {
      const isSelected = btn.dataset.frameColor === color;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', String(isSelected));
    });

    // Uppdatera heading-värdet ("Ram: <vald-färg>") så det matchar
    // Habitats variant-picker-mönster ("Storlek: 70x100 cm"). Texten
    // hämtas från knappens data-frame-display-name (sätts via Liquid t-helper).
    const selectedBtn = root.querySelector(`.frame-picker__option[data-frame-color="${color}"]`);
    const displayName = selectedBtn?.getAttribute('data-frame-display-name') || '';
    const labelNode = root.querySelector('[data-frame-current-label]');
    if (labelNode && displayName) labelNode.textContent = displayName;

    if (hasFramedSlides) {
      showFramedSlide(color, fromClick);
    } else if (heroImg) {
      if (color === 'none') {
        if (originalHeroSrc) heroImg.setAttribute('src', originalHeroSrc);
        if (originalHeroSrcset !== null) {
          if (originalHeroSrcset) heroImg.setAttribute('srcset', originalHeroSrcset);
          else heroImg.removeAttribute('srcset');
        }
      } else {
        const url = (window.__frameImages || {})[color];
        if (url) {
          heroImg.setAttribute('src', url);
          // srcset på orig-bilden kan peka på canvas-CDN:n och ta över src — rensa.
          heroImg.removeAttribute('srcset');
        }
      }
    }

    applyTotalPrice();
    syncBuyNowVisibility();
    updatePriceDisplay();
  }

  root.addEventListener('click', (event) => {
    const btn = event.target.closest('.frame-picker__option');
    if (!btn || !root.contains(btn)) return;
    if (btn.classList.contains('is-disabled')) {
      event.preventDefault();
      return;
    }
    selectFrame(btn.dataset.frameColor, true);
  });

  // ─── CART ADD HOOK ──────────────────────────────────────────────────
  //
  // Habitats <product-form> (assets/product.js rad 559+) registrerar en
  // submit-handler i constructor som preventDefault + fetchar /cart/add
  // med canvas-only. Vi MÅSTE köra först och blockera den när en ram
  // är vald.
  //
  // Strategi (lärt av tidigare misslyckad fix på productForm-element):
  //   1. Lyssna på `submit` på `document` i CAPTURE phase. Capture körs
  //      från document → target, så vi fångar alla submits oavsett vilket
  //      form-element det är. Mindre känsligt för selector-mismatches.
  //   2. stopImmediatePropagation() blockerar Habitats handler.
  //   3. För dynamic checkout-knappen ("Köp nu" / Shop Pay) — den triggar
  //      INTE submit, går runt oss helt. Vi DÖLJER den när ram är vald
  //      (CSS via .frame-picker-buy-now-hidden på body) och visar text om
  //      att kunden måste använda "Lägg i kundvagn".

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    const action = form.getAttribute('action') || '';
    if (/\/cart\/add(\?|$)/.test(action)) return true;
    if (form.closest && form.closest('product-form')) return true;
    if (form.dataset && form.dataset.type === 'add-to-cart-form') return true;
    return false;
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!isCartAddForm(form)) return;
    if (currentFrame === 'none') return; // canvas-only — låt Habitat hantera

    const canvasSize = root.getAttribute('data-canvas-size');
    const frameKey = getFrameKey(canvasSize);
    const variants = window.__frameVariants || {};
    const frameVariant = variants[currentFrame] && variants[currentFrame][frameKey];

    if (!frameVariant || !frameVariant.id) {
      // Blockera submit — att släppa igenom här lägger canvas UTAN ram i
      // varukorgen medan UI:t fortfarande visar ram vald + ram-pris.
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      console.error('[frame-picker] Saknar ram-variant för', currentFrame, canvasSize);
      alert(root.getAttribute('data-err-frame-missing') || 'Den valda ramen är tyvärr inte tillgänglig för den här storleken just nu. Välj en annan ramfärg eller "Ingen" och försök igen.');
      selectFrame('none');
      return;
    }

    // Blockera Habitats canvas-only-fetch
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    const formData = new FormData(form);
    const canvasVariantId = formData.get('id');
    const canvasQty = parseInt(formData.get('quantity') || '1', 10);

    if (!canvasVariantId) {
      console.error('[frame-picker] [name="id"] saknas/disabled i formData — kan inte addera');
      alert(root.getAttribute('data-err-variant-missing') || 'Kunde inte läsa vald variant. Försök igen.');
      return;
    }

    // Motivnamnet till ramradens _canvas_companion_label, som tryckeriet
    // läser för att veta vilken duk som ska i vilken ram (två dukar i samma
    // storlek + två ramfärger i en order). Läses ur data-canvas-title som
    // Liquid renderar från product.title. Den tidigare DOM-selektorn
    // ('.product-single__title, h1.product__title') matchade aldrig Habitats
    // <h1 class="product-title">, så etiketten blev bara storleken på alla
    // ramordrar maj–okt 2026. Utredning:
    // docs/frame-pipeline/investigations/2026-10-20-ramrad-saknar-motivnamn.md
    const productTitle = (
      root.getAttribute('data-canvas-title') ||
      document.querySelector('h1.product-title')?.textContent ||
      ''
    ).trim();

    const items = [
      { id: canvasVariantId, quantity: canvasQty },
      {
        id: frameVariant.id,
        quantity: canvasQty,
        properties: {
          '_canvas_companion_variant': String(canvasVariantId),
          '_canvas_companion_label': productTitle ? `${productTitle} ${canvasSize}` : canvasSize
        }
      }
    ];

    // Skicka med `sections` så Shopify returnerar uppdaterad cart-HTML i
    // samma response — då kan vi anropa Habitats renderContents() och få
    // cart-drawern att öppnas direkt (samma flow som vid canvas-only
    // add-to-cart). Sektionsnamnen kommer från Habitats getSectionsToRender
    // i assets/product.js rad 620-636.
    const sections = ['main-cart', 'cart-drawer', 'cart-bubble'];

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      });
      const respBody = await resp.text();
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${respBody}`);

      const parsedState = JSON.parse(respBody);

      // Kalla Habitats <product-form>-instans renderContents — den
      // ersätter cart-drawer-DOM med ny innehåll och öppnar drawern via
      // `new CartDrawer()`-anropet i sin loop. Samma flow som vid
      // canvas-only submit, så UX är konsistent.
      const productFormEl = form.closest('product-form') || document.querySelector('product-form');
      if (productFormEl && typeof productFormEl.renderContents === 'function') {
        productFormEl.renderContents(parsedState);
      } else {
        // Fallback: navigera till /cart om Habitat-elementet inte är
        // upgraded än (osannolikt, men vi tappar inte ramen då).
        console.warn('[frame-picker] product-form.renderContents saknas — fallback till /cart');
        window.location.href = '/cart';
      }
    } catch (err) {
      console.error('[frame-picker] Cart bulk add failed', err);
      alert(root.getAttribute('data-err-cart-add') || 'Något gick fel när vi lade tavlan + ramen i kundvagnen. Försök igen, eller välj "Ingen" som ramfärg och köp ramen separat.');
    }
  }, true); // capture på document — mest pålitlig position

  // ─── DYNAMIC CHECKOUT ("Köp nu") — gömma när ram är vald ────────────
  //
  // Shopifys dynamic checkout-knapp triggar inte form submit; den hoppar
  // direkt till checkout med bara den valda canvas-varianten. När en ram
  // är vald måste vi förhindra detta — annars går ramen förlorad i kassan.
  // Lösning: dölj knappen via body-klass + CSS i frame-picker.css.
  function syncBuyNowVisibility() {
    if (currentFrame === 'none') {
      document.body.classList.remove('frame-picker-buy-now-hidden');
    } else {
      document.body.classList.add('frame-picker-buy-now-hidden');
    }
  }

  // ─── PRISKONVERGENS MOT HABITATS SECTION-RENDER ─────────────────────
  //
  // Habitats VariantSelects.renderProductInfo() fetchar vid varje
  // variantbyte server-renderad HTML (?variant=X&section_id=Y) och
  // ersätter #price-<section> innerHTML — alltid canvas-only (servern
  // känner inte ramvalet) och utan något event efteråt. Fetchen tar
  // 150–800 ms; en engångsskrivning från oss (50 ms-timern nedan)
  // skrevs därför över och priset "hoppade tillbaka" till canvas-only.
  // Utredning: docs/frame-pipeline/investigations/2026-08-13-frame-picker-pris-race.md
  //
  // Fix: observera pris-containern (den yttre diven överlever — Habitat
  // ersätter bara dess INNEHÅLL) och re-applicera totalpriset när någon
  // annan skriver. applyTotalPrice är write-if-different, så vår egen
  // skrivning genererar inga nya mutationer → konvergens, aldrig loop.
  // Vid currentFrame === 'none' äger serverns render priset (korrekt
  // canvas-only i serverns eget format) — då ingriper vi inte.
  document.querySelectorAll('[id^="price-"]').forEach((container) => {
    new MutationObserver(() => {
      if (currentFrame !== 'none') applyTotalPrice();
    }).observe(container, { childList: true, subtree: true, characterData: true });
  });

  // Variant-byten: Habitat firar `product:variant-change` (verifierat i
  // assets/product.js rad 39-42 — dispatchCustomEvent). setTimeout 50ms
  // ger Habitats synkrona DOM-uppdateringar (input value, radios) tid
  // att landa innan compat-state/pris läses om. Prisets skydd mot den
  // ASYNKRONA section-rendern är observern ovan — inte denna timer.
  // ─── MARKET-PRISER ──────────────────────────────────────────────────
  //
  // __frameVariants bakas av Liquid via all_products[handle], vars priser
  // renderas i butikens BASvaluta (SEK) — inte marknadens. På externa
  // markets (EUR m.fl.) blir både "+ pris"-displayen och totalpriset fel,
  // trots att cart/checkout debiterar rätt konverterat belopp. AJAX-
  // endpointen /products/{handle}.js returnerar däremot presentment-pris
  // i aktiv market-kontext (samma belopp som cart), så vi skriver över
  // Liquid-priserna med den datan vid load. Nätverksfel → Liquid-datan
  // står kvar som fallback. På .se är beloppen identiska (no-op).
  function refreshMarketPrices() {
    const rootUrl = window.__frameRoutesRoot || '/';
    const handleByColor = { 'ljus-ek': 'ram-ljus-ek', 'valnot': 'ram-valnot' };
    Object.keys(handleByColor).forEach((color) => {
      fetch(rootUrl + 'products/' + handleByColor[color] + '.js')
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (!p || !p.variants) return;
          const variants = (window.__frameVariants || {})[color] || {};
          Object.keys(variants).forEach((size) => {
            const entry = variants[size];
            const live = p.variants.find((v) => String(v.id) === String(entry.id));
            if (live && typeof live.price === 'number') {
              entry.price = live.price;
              entry.available = live.available;
            }
          });
          updatePriceDisplay();
          updateStockNotice();
          if (currentFrame !== 'none') applyTotalPrice();
        })
        .catch(() => {});
    });
  }
  refreshMarketPrices();

  document.addEventListener('product:variant-change', () => setTimeout(syncCanvasSize, 50));
  const variantInput = document.querySelector('[name="id"]');
  if (variantInput) {
    variantInput.addEventListener('change', () => setTimeout(syncCanvasSize, 50));
  }
  // Habitats variant-radios
  document.querySelectorAll('input[name^="options"]').forEach((el) => {
    el.addEventListener('change', () => setTimeout(syncCanvasSize, 50));
  });
  document.querySelectorAll('select[name^="options"]').forEach((el) => {
    el.addEventListener('change', () => setTimeout(syncCanvasSize, 50));
  });

  syncCanvasSize();
})();
